import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  alpha,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  FormControl,
  Select,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  ListItemButton,
  Paper,
  Popper,
  ClickAwayListener,
  MenuList,
  Chip,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  DirectionsCar as VehiclesIcon,
  BarChart as BarChartIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  Assessment as ReportsIcon,
  TrackChanges as TrackingIcon,
  History as HistoryIcon,
  VideoLibrary as VideoIcon,
  Notifications as AlertsIcon,
  Inventory as InventoryIcon,
  AddCircle as AddDeviceIcon,
  Speed as SpeedIcon,
  Route as RouteIcon,
  Schedule as ScheduleIcon,
  TripOrigin as TripIcon,
  Place as GeofenceIcon,
  SupervisorAccount as UserManagementIcon,
  Security as SecurityIcon,
  AdminPanelSettings as RoleIcon,
  Support as SupportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Palette as PaletteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../provider/UserProvider';
import { useNotification } from '../../context/NotificationContext';
import axiosInstance from '../../utlis/axiosInstance';

const Navbar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAnchors, setMenuAnchors] = useState<{ [key: string]: HTMLElement | null }>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { notificationToken, requestPermission, showNotification } = useNotification();
  const [notificationOn, setNotificationOn] = useState<boolean>(!!notificationToken);
  const [notifLoading, setNotifLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [isTranslateReady, setIsTranslateReady] = useState<boolean>(false);
  const applyLangRetryRef = useRef<NodeJS.Timeout | null>(null);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);

  // Google Translate loader
  React.useEffect(() => {
    const elementId = 'google_translate_element';
    const bodyContainerId = 'google_translate_body_container';
    let pollId: number | null = null;
    // Read cookie language upfront to avoid defaulting to 'en' during init
    let initialCookieLang = 'en';
    try {
      initialCookieLang = readCookieLang();
      setSelectedLanguage(initialCookieLang);
    } catch (_) { /* noop */ }

    function initTranslate() {
      if (window.google && window.google.translate) {
        // Ensure a hidden container exists where Google will mount its widget
        let targetContainer = document.getElementById(elementId);
        if (!targetContainer) {
          targetContainer = document.createElement('div');
          targetContainer.id = elementId;
          targetContainer.style.position = 'absolute';
          targetContainer.style.left = '-9999px';
          targetContainer.style.top = '0';
          targetContainer.style.width = '200px';
          targetContainer.style.height = '40px';
          document.body.appendChild(targetContainer);
        }
        // eslint-disable-next-line no-new
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,hi,mr,gu,ml,ta,te,kn',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          elementId,
        );
        setIsTranslateReady(true);
      }
    }

    // Hide Google banner and watermark for a cleaner UI
    const ensureTranslateStyles = () => {
      if (!document.getElementById('google-translate-custom-styles')) {
        const style = document.createElement('style');
        style.id = 'google-translate-custom-styles';
        style.innerHTML = `
          .goog-logo-link { display: none !important; }
          .goog-te-gadget { font-size: 0 !important; }
          .goog-te-banner-frame { display: none !important; }
          body { top: 0 !important; }
        `;
        document.head.appendChild(style);
      }
    };
    ensureTranslateStyles();

    // Observe when the translate combo appears (watch the whole document for reliability)
    const container = document.body || document.getElementById(elementId) || document.getElementById(bodyContainerId);
    let observer: MutationObserver | null = null;
    if (container) {
      observer = new MutationObserver(() => {
        const combo = document.querySelector('select.goog-te-combo');
        if (combo) {
          setIsTranslateReady(true);
          applyLanguage(selectedLanguage, 5);
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    }

    // If already loaded, just init
    if (window.google && window.google.translate) {
      initTranslate();
      return;
    }

    // Avoid injecting multiple times
    const existingScript = document.getElementById('google-translate-script');
    if (existingScript) {
      // Will call the global callback when ready
      window.googleTranslateElementInit = initTranslate;
      return;
    }

    // Create global callback and inject script
    window.googleTranslateElementInit = initTranslate;
    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Do not remove script to preserve widget across navigations
      if (observer) observer.disconnect();
      if (pollId) {
        window.clearInterval(pollId);
        pollId = null;
      }
    };
  }, []);

  const availableLanguages: { code: string; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'Hindi' },
    { code: 'mr', label: 'Marathi' },
    { code: 'gu', label: 'Gujarati' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'ta', label: 'Tamil' },
    { code: 'te', label: 'Telugu' },
    { code: 'kn', label: 'Kannada' },
  ];

  const getGoogleCombo = () => document.querySelector('select.goog-te-combo') as HTMLSelectElement | null;

  const setGoogTransCookie = (targetLang: string) => {
    try {
      const value = `/en/${targetLang}`;
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `googtrans=${encodeURIComponent(value)}; expires=${expires}; path=/`;
      document.cookie = `googtrans=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=${window.location.hostname}`;
    } catch (_) { /* noop */ }
  };

  const readCookieLang = () => {
    const match = document.cookie.match(/(?:^|; )googtrans=([^;]+)/);
    if (!match) return 'en';
    try {
      const val = decodeURIComponent(match[1]);
      const parts = val.split('/');
      const langCode = parts[2] || 'en';
      return availableLanguages.some((l) => l.code === langCode) ? langCode : 'en';
    } catch (_) {
      return 'en';
    }
  };

  const applyLanguage = (langCode: string, retries: number = 0) => {
    const combo = getGoogleCombo();
    if (combo) {
      combo.value = langCode;
      const evt = new Event('change', { bubbles: true });
      combo.dispatchEvent(evt);
      if (typeof (combo as any).onchange === 'function') {
        (combo as any).onchange();
      }
      if (applyLangRetryRef.current) {
        clearTimeout(applyLangRetryRef.current);
        applyLangRetryRef.current = null;
      }
      setSelectedLanguage(langCode);
      return;
    }

    // Fallback: persist via cookie and try to init/apply, then reload if needed
    setGoogTransCookie(langCode);
    setSelectedLanguage(langCode);
    try { (window as any).googleTranslateElementInit?.(); } catch (_) { /* noop */ }

    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      const c = getGoogleCombo();
      if (c) {
        c.value = langCode;
        c.dispatchEvent(new Event('change', { bubbles: true }));
        window.clearInterval(id);
      } else if (tries > 40) {
        window.clearInterval(id);
        window.location.reload();
      }
    }, 150);

    if (retries > 0) {
      if (applyLangRetryRef.current) clearTimeout(applyLangRetryRef.current);
      applyLangRetryRef.current = setTimeout(() => applyLanguage(langCode, retries - 1), 300);
    }
  };

  const handleLanguageChange = (event: any) => {
    const lang = event.target.value as string;
    // If translate widget not ready yet, retry a few times
    try { setGoogTransCookie(lang); } catch (_) { /* noop */ }
    applyLanguage(lang, isTranslateReady ? 3 : 10);
  };

  // Initialize selected language from cookie on mount
  React.useEffect(() => {
    try {
      const cookieLang = readCookieLang();
      setSelectedLanguage(cookieLang);
    } catch (_) { /* noop */ }
  }, []);



  React.useEffect(() => {
    setNotificationOn(!!notificationToken);
  }, [notificationToken]);

  // Listen for theme changes and force re-render
  React.useEffect(() => {
    const handleThemeChange = () => {
      // Force re-render when theme changes
      setAnchorEl(null); // Close any open menus
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  // Close all dropdowns when route changes
  React.useEffect(() => {
    closeAllDropdowns();
  }, [location.pathname]);

  // Close all dropdowns on scroll
  React.useEffect(() => {
    const handleScroll = () => {
      closeAllDropdowns();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDropdownHover = (event: React.MouseEvent<HTMLElement>, key: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Immediately open this dropdown and close others
    setMenuAnchors({
      [key]: event.currentTarget,
    });
  };

  const handleDropdownClose = (key: string) => {
    setMenuAnchors((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const closeAllDropdowns = () => {
    // Clear any pending hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setMenuAnchors({});
  };

  const isDropdownOpen = (key: string) => Boolean(menuAnchors[key]);

  const isParentActive = (subItems: any[]) => {
    return subItems.some((item) => location.pathname === item.path);
  };

  const getIconColor = (path: string) => {
    const colorMap: { [key: string]: string } = {
      // Fleet Management Colors
      '/vehicles': '#3B82F6', // Blue
      '/vehicle-tracking': '#10B981', // Green
      '/add-device': '#8B5CF6', // Purple
      '/inventory': '#F59E0B', // Amber
      '/history-playback': '#EF4444', // Red
      '/video-recording': '#EC4899', // Pink

      // Reports Colors
      '/reports': '#6366F1', // Indigo
      '/reports/trip': '#14B8A6', // Teal
      '/reports/route': '#F97316', // Orange (Position Report)
      '/reports/stop': '#84CC16', // Lime
      '/reports/summary': '#06B6D4', // Cyan
      '/reports/ignition': '#8B5CF6', // Purple
      '/reports/event': '#EF4444', // Red
      '/reports/geofencing': '#10B981', // Green

      // Geofencing Colors
      '/geofencing': '#059669', // Emerald

      // User Management Colors
      '/users': '#3B82F6', // Blue
      '/add-users': '#10B981', // Green
      '/role-management': '#F59E0B', // Amber
      '/permission-management': '#EF4444', // Red
      '/groups': '#8B5CF6', // Purple

      // Settings Colors
      '/settings': '#6366F1', // Indigo
      '/plans': '#8B5CF6', // Purple
      '/points-history': '#F59E0B', // Amber
      '/points-transfer': '#EC4899', // Pink
      '/support-tickets': '#10B981', // Green

      // Alerts Colors
      '/all-alerts': '#EF4444', // Red
      '/notification-management': '#6B7280', // Gray
    };

    return colorMap[path] || '#6B7280'; // Default gray
  };

  const handleNotificationToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setNotifLoading(true);
    try {
      if (checked) {
        // Turn ON: request permission and save token
        const token = await requestPermission();
        if (token) {
          try {
            await axiosInstance.post('/gpstracker/firebasehandler/', {
              token,
              device_type: 'web',
            });
            showNotification('Notifications Enabled', 'You will receive push notifications.', 'success');
            setNotificationOn(true);
          } catch (err) {
            showNotification('Error', 'Failed to save FCM token. Please try again.', 'error');
            setNotificationOn(false);
          }
        } else {
          showNotification('Permission Denied', 'Notification permission was not granted.', 'error');
          setNotificationOn(false);
        }
      } else {
        // Turn OFF: delete token from backend
        if (notificationToken) {
          try {
            await axiosInstance.delete('/gpstracker/firebasehandler/', {
              data: { token: notificationToken, device_type: 'web' },
            });
            showNotification('Notifications Disabled', 'You will not receive push notifications.', 'info');
            setNotificationOn(false);
          } catch (err) {
            showNotification('Error', 'Failed to delete FCM token. Please try again.', 'error');
          }
        } else {
          setNotificationOn(false);
        }
      }
    } catch (err) {
      showNotification('Error', 'Failed to update notification settings.', 'error');
    } finally {
      setNotifLoading(false);
    }
  };

  const navItems = [
    {
      label: 'Dashboard',
      path: '/',
      type: 'single',
    },
    {
      label: 'Vehicles',

      type: 'dropdown',
      key: 'fleet',
      subItems: [
        { label: 'All Vehicles', path: '/vehicles', icon: <VehiclesIcon /> },
        // { label: "Vehicle Tracking", path: "/vehicle-tracking", icon: <TrackingIcon /> },
        { label: 'Add New Device', path: '/add-device', icon: <AddDeviceIcon /> },
        { label: 'Inventory', path: '/inventory', icon: <InventoryIcon /> },
        { label: 'Disabled Devices', path: '/disabled-devices', icon: <VehiclesIcon /> },
        // { label: "History Playback", path: "/history-playback", icon: <HistoryIcon /> },
        // { label: "Video Recording", path: "/video-recording", icon: <VideoIcon /> },
      ],
    },
    {
      label: 'Reports',
      type: 'dropdown',
      key: 'reports',
      subItems: [
        { label: 'All Reports', path: '/reports', icon: <BarChartIcon /> },
        { label: 'Trip Report', path: '/reports/trip', icon: <TripIcon /> },
        { label: 'Position Report', path: '/reports/route', icon: <RouteIcon /> },
        { label: 'Stop Report', path: '/reports/stop', icon: <ScheduleIcon /> },
        { label: 'Summary Report', path: '/reports/summary', icon: <ReportsIcon /> },
        { label: 'Ignition Report', path: '/reports/ignition', icon: <SpeedIcon /> },
        { label: 'Event Report', path: '/reports/event', icon: <AlertsIcon /> },
        { label: 'Geofencing Report', path: '/reports/geofencing', icon: <GeofenceIcon /> },
      ],
    },
    {
      label: 'Geofence',
      type: 'dropdown',
      key: 'geofencing',
      subItems: [
        { label: 'Manage Geofences', path: '/geofencing', icon: <GeofenceIcon /> },
        // { label: "Geofence Reports", path: "/reports/geofencing", icon: <ReportsIcon /> },
      ],
    },
    // {
    //   label: "Alerts",
    //   type: "dropdown",
    //   key: "alerts",
    //   subItems: [
    //     { label: "All Alerts", path: "/all-alerts", icon: <AlertsIcon /> },
    //     { label: "Notification Management", path: "/notification-management", icon: <SettingsIcon /> },
    //   ],
    // },
    {
      label: 'Users',
      type: 'dropdown',
      key: 'users',
      subItems: [
        { label: 'Manage Users', path: '/users', icon: <PersonIcon /> },
        // { label: "Add Users", path: "/add-users", icon: <AddDeviceIcon /> },
        { label: 'Role Management', path: '/role-management', icon: <RoleIcon /> },
        { label: 'Permissions', path: '/permission-management', icon: <SecurityIcon /> },
        // { label: "Group Management", path: "/groups", icon: <GroupsIcon /> },
      ],
    },
    {
      label: 'Settings',
      type: 'dropdown',
      key: 'settings',
      subItems: [
        { label: 'General Settings', path: '/settings', icon: <SettingsIcon /> },
        // { label: "Role Management", path: "/role-management", icon: <RoleIcon /> },
        { label: 'Group Management', path: '/groups', icon: <GroupsIcon /> },
        { label: 'Subscription Plans', path: '/plans', icon: <MoneyIcon /> },
        // { label: "Geofencing", path: "/geofencing", icon: <GeofenceIcon /> },
        { label: 'Points History', path: '/points-history', icon: <TrophyIcon /> },
        { label: 'Points Transfer', path: '/points-transfer', icon: <StarIcon /> },
        { label: 'Support Tickets', path: '/support-tickets', icon: <SupportIcon /> },
      ],
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // For exact matching of paths to avoid conflicts like /pricing and /pricing-management
    return location.pathname === path;
  };

  const handleLogout = () => {
    // Clear all localStorage data
    localStorage.clear();
    // Redirect to login page
    navigate('/login');
  };

  // Check if impersonating on mount and when localStorage changes
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem('isImpersonating') === 'true';
      setIsImpersonating(impersonating);
    };
    
    checkImpersonation();
    
    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'isImpersonating') {
        checkImpersonation();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case of same-tab changes
    const interval = setInterval(checkImpersonation, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Exit impersonation function
  const handleExitImpersonation = async () => {
    // Get saved credentials from localStorage (saved during normal login)
    const savedEmail = localStorage.getItem('savedEmail');
    const savedPassword = localStorage.getItem('savedPassword');
    
    if (!savedEmail || !savedPassword) {
      console.error('Original user credentials not found.');
      localStorage.removeItem('isImpersonating');
      window.location.href = '/login';
      return;
    }

    try {
      // Login back as original user using saved credentials
      const loginPayload = {
        email: savedEmail.trim(),
        password: savedPassword
      };
      
      console.log('Returning to original user:', { email: loginPayload.email, passwordLength: loginPayload.password?.length });
      
      const response = await axiosInstance.post(
        '/gpstracker/usersessionhandler/',
        loginPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const responseData = response.data;
      let parsedData;
      
      if (typeof responseData === 'string') {
        try {
          const cleanedData = responseData.replace(/Infinity/g, 'null');
          parsedData = JSON.parse(cleanedData);
        } catch (parseError) {
          console.error('Error parsing string response:', parseError);
          return;
        }
      } else if (typeof responseData === 'object' && responseData !== null) {
        parsedData = responseData;
      } else {
        return;
      }

      if (parsedData.responseData) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(parsedData.responseData));
        localStorage.removeItem('isImpersonating');
        
        setIsImpersonating(false);
        
        // Dispatch custom login event to trigger theme reload
        window.dispatchEvent(new CustomEvent('userLogin'));
        
        // Reload the page to apply the original user session
        window.location.href = '/';
      }
    } catch (error: any) {
      console.error('Error exiting impersonation:', error);
    }
  };

  return (
    <AppBar
      position='sticky'
      elevation={0}
      sx={{
        background: 'var(--primary-color)',
        borderBottom: '1px solid',
        borderColor: alpha('#ffffff', 0.1),
        boxShadow: '0 4px 20px rgba(0, 80, 167, 0.15)',
        backdropFilter: 'blur(20px)',
        zIndex: 1000,
        overflow: 'visible',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: '',
          // animation: "shimmer 3s ease-in-out infinite",
        },
        '@keyframes shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      }}
    >
      <Container
        maxWidth='xl'
        sx={{ overflow: 'visible' }}
      >
        <Toolbar sx={{ px: 0, py: 1, overflow: 'visible' }}>
          {/* Logo/Brand */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 6,
              cursor: 'pointer',
              marginRight: {
                xs:'0px',
                lg:'48px'
              },
              // transition: "all 0.3s ease-in-out",
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                // background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                borderRadius: 3,
                px: 2,
                py: 1,
                // border: "1px solid",
                // borderColor: alpha("#ffffff", 0.2),
                // backdropFilter: "blur(10px)",
              }}
            >
              <img
                src='/logo.png'
                alt='Acumen GPS Logo'
                style={{
                  height: '44px',
                  width: 'auto',
                  filter: 'brightness(1.1)',
                }}
              />
              {/* <Typography
                variant="h6"
                sx={{
                  ml: 2,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  letterSpacing: "0.5px",
                  display: { xs: "none", md: "block" },
                }}
              >
                ACUMEN GPS
              </Typography> */}
            </Box>
          </Box>

          {/* Navigation Items */}
          <ClickAwayListener onClickAway={closeAllDropdowns}>
            <Box
              onMouseLeave={() => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = null;
                }
              }}
              sx={{
                display: { xs: 'none', lg: 'flex' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              {navItems.map((item) => {
                if (item.type === 'single') {
                  return (
                    <Button
                      key={item.path}
                      onClick={() => navigate(item.path!)}
                      // startIcon={item.icon}
                      sx={{
                        color: isActive(item.path!) ? '#0050A7' : 'white',
                        backgroundColor: isActive(item.path!) ? 'white' : 'transparent',
                        fontWeight: isActive(item.path!) ? 600 : 500,
                        px: 3,
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        // transition: "all 0.2s ease-in-out",
                        '&:hover': {
                          backgroundColor: isActive(item.path!) ? 'white' : alpha('#ffffff', 0.12),
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        '& .MuiButton-startIcon': {
                          color: isActive(item.path!) ? 'var(--primary-color)' : 'white',
                          marginRight: 1,
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                }

                if (item.type === 'dropdown') {
                  const isOpen = isDropdownOpen(item.key!);
                  const hasActiveChild = isParentActive(item.subItems!);

                  return (
                    <Box
                      key={item.key}
                      sx={{ position: 'relative' }}
                    >
                      <Button
                        onMouseEnter={(e) => handleDropdownHover(e, item.key!)}
                        // startIcon={item.icon}
                        endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{
                          color: hasActiveChild ? '#0050A7' : 'white',
                          backgroundColor: hasActiveChild ? 'white' : isOpen ? alpha('#ffffff', 0.12) : 'transparent',
                          fontWeight: hasActiveChild ? 600 : 500,
                          px: 3,
                          py: 1.2,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          // transition: "all 0.2s ease-in-out",
                          '&:hover': {
                            backgroundColor: hasActiveChild ? 'white' : alpha('#ffffff', 0.12),
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          },
                          '& .MuiButton-startIcon': {
                            color: hasActiveChild ? '#0050A7' : 'white',
                            marginRight: 1,
                          },
                          '& .MuiButton-endIcon': {
                            color: hasActiveChild ? '#0050A7' : 'white',
                            marginLeft: 1,
                          },
                        }}
                      >
                        {item.label}
                      </Button>

                      <Popper
                        open={isOpen}
                        anchorEl={menuAnchors[item.key!]}
                        placement='bottom-start'
                        sx={{
                          zIndex: 9999,
                          position: 'relative',
                        }}
                        modifiers={[
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 12],
                            },
                          },
                          {
                            name: 'preventOverflow',
                            options: {
                              boundary: 'viewport',
                            },
                          },
                        ]}
                      >
                        <ClickAwayListener onClickAway={() => handleDropdownClose(item.key!)}>
                          <Paper
                            onMouseLeave={() => {
                              // Close dropdown when mouse leaves the menu
                              handleDropdownClose(item.key!);
                            }}
                            sx={{
                              minWidth: 280,
                              borderRadius: 4,
                              boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                              border: '1px solid',
                              borderColor: alpha('#000', 0.08),
                              overflow: 'hidden',
                              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                              position: 'relative',
                              zIndex: 10000,
                            }}
                          >
                            <MenuList
                              sx={{
                                py: 1.5,
                                '& .MuiMenuItem-root': {
                                  borderRadius: 2,
                                  mx: 1,
                                  my: 0.5,
                                  px: 2,
                                  py: 1.5,
                                  // transition: "all 0.2s ease-in-out",
                                  borderLeft: '3px solid transparent',
                                  '&:hover': {
                                    backgroundColor: alpha('#0050A7', 0.08),
                                    borderRadius: 2,
                                    transform: 'translateX(4px)',
                                    borderLeftColor: alpha('#0050A7', 0.3),
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  },
                                },
                              }}
                            >
                              {item.subItems!.map((subItem) => (
                                <MenuItem
                                  key={subItem.path}
                                  onClick={() => {
                                    navigate(subItem.path);
                                    closeAllDropdowns();
                                  }}
                                  sx={{
                                    backgroundColor: isActive(subItem.path) ? alpha('#0050A7', 0.12) : 'transparent',
                                    borderLeftColor: isActive(subItem.path) ? getIconColor(subItem.path) : 'transparent',
                                    borderLeftWidth: isActive(subItem.path) ? '3px' : '0px',
                                    '&:hover': {
                                      backgroundColor: alpha('#0050A7', 0.15),
                                      '& .MuiSvgIcon-root': {
                                        transform: 'scale(1.1)',
                                        filter: 'brightness(1.3) saturate(1.2)',
                                      },
                                    },
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      minWidth: 28,
                                      '& .MuiSvgIcon-root': {
                                        color: isActive(subItem.path) ? '#0050A7' : getIconColor(subItem.path),
                                        fontSize: '1.2rem',
                                        //  transition: "all 0.2s ease-in-out",
                                        filter: isActive(subItem.path) ? 'none' : 'brightness(1.1)',
                                      },
                                    }}
                                  >
                                    {subItem.icon}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={subItem.label}
                                    sx={{
                                      '& .MuiListItemText-primary': {
                                        fontSize: '0.9rem',
                                        fontWeight: isActive(subItem.path) ? 600 : 500,
                                        color: isActive(subItem.path) ? '#0050A7' : '#374151',
                                      },
                                    }}
                                  />
                                  {isActive(subItem.path) && (
                                    <ChevronRightIcon
                                      sx={{
                                        color: '#0050A7',
                                        fontSize: 18,
                                        ml: 1,
                                      }}
                                    />
                                  )}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Paper>
                        </ClickAwayListener>
                      </Popper>
                    </Box>
                  );
                }

                return null;
              })}
            </Box>
          </ClickAwayListener>

          {/* Mobile Menu Button */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                color: 'white',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                border: '1px solid',
                borderColor: alpha('#ffffff', 0.2),
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Notifications Shortcut */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
            <IconButton
              aria-label='All Alerts'
              onClick={() => navigate('/all-alerts')}
              sx={{
                color: 'white',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                border: '1px solid',
                borderColor: alpha('#ffffff', 0.2),
                backdropFilter: 'blur(10px)',
                mr: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                },
              }}
            >
              <AlertsIcon />
            </IconButton>
          </Box>

          {/* Exit Impersonation Icon */}
          {isImpersonating && (
            <Tooltip title="Exit impersonation and return to original account" arrow>
              <IconButton
                onClick={handleExitImpersonation}
                sx={{
                  color: '#fff',
                  background: 'rgba(245, 158, 11, 0.2)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  mr: 1,
                  '&:hover': {
                    background: 'rgba(245, 158, 11, 0.3)',
                    borderColor: 'rgba(245, 158, 11, 0.6)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                color: '#fff',
                px: 2.5,
                py: 1.5,
                borderRadius: 3,
                background: 'transparent',
                boxShadow: 'none',
                // transition: "0.3s ease-in-out",
                '&:hover': {
                  background: 'rgba(255,255,255,0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Avatar
                sx={{
                  background: '#ffffff',
                  color: '#0050A7',
                  width: 42,
                  height: 42,
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                }}
              >
                {user?.user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ textAlign: 'left', display: { xs: 'none',  } }}>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', textTransform: 'capitalize' }}>{user?.user?.name || 'User'}</Typography>
                <Typography sx={{ color: alpha('#ffffff', 0.75), fontSize: '0.75rem' }}>{user?.user?.email || 'user@example.com'}</Typography>
              </Box>
              <ExpandMoreIcon sx={{ color: '#fff', fontSize: 20 }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 2,
                  minWidth: 280,
                  borderRadius: 3,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  background: '#ffffff',
                },
              }}
            >
              <MenuItem
                sx={{
                  py: 2.5,
                  px: 3,
                  cursor: 'default',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
                  <Avatar
                    sx={{
                      background: '#0050A7',
                      width: 48,
                      height: 48,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      color: '#fff',
                    }}
                  >
                    {user?.user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      style={{ textTransform: 'capitalize' }}
                      fontWeight={700}
                      color='#1f2937'
                      fontSize='1rem'
                    >
                      {user?.user?.name || 'User'}
                    </Typography>
                    <Typography
                      fontSize='0.85rem'
                      color='#6b7280'
                    >
                      {user?.user?.email || 'user@example.com'}
                    </Typography>
                    <Chip
                      label='Premium User'
                      size='small'
                      sx={{
                        mt: 1,
                        background: '#10b981',
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        px: 1,
                      }}
                    />
                  </Box>
                </Box>
              </MenuItem>

              <Divider />

              <MenuItem sx={{ py: 2, px: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: notificationOn ? '#10b981' : '#6b7280',
                      color: '#fff',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <AlertsIcon fontSize='small' />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      fontWeight={600}
                      fontSize='0.9rem'
                      color='#374151'
                    >
                      Push Notifications
                    </Typography>
                    <Typography
                      variant='caption'
                      color='#6b7280'
                    >
                      {notificationOn ? 'Real-time alerts enabled' : 'Notifications disabled'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={notificationOn}
                    onChange={handleNotificationToggle}
                    disabled={notifLoading}
                    sx={{
                      '& .MuiSwitch-track': {
                        backgroundColor: notificationOn ? '#10b981' : '#e5e7eb',
                      },
                      '& .MuiSwitch-thumb': {
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </Box>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  navigate('/theme-management');
                  handleMenuClose();
                }}
                sx={{
                  py: 2,
                  px: 3,
                  gap: 2,
                  mx: 1,
                  borderRadius: 2,
                  color: '#374151',
                  // transition: "all 0.25s ease-in-out",
                  '&:hover': {
                    backgroundColor: alpha('#0050A7', 0.08),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: '#8B5CF6',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PaletteIcon fontSize='small' />
                </Box>
                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize='0.9rem'
                  >
                    Theme Management
                  </Typography>
                  <Typography
                    variant='caption'
                    color='#6b7280'
                  >
                    Customize your app appearance
                  </Typography>
                </Box>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 2,
                  px: 3,
                  gap: 2,
                  mx: 1,
                  mb: 1,
                  borderRadius: 2,
                  color: '#dc2626',
                  // transition: "all 0.25s ease-in-out",
                  '&:hover': {
                    backgroundColor: alpha('#dc2626', 0.08),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: '#dc2626',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon fontSize='small' />
                </Box>
                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize='0.9rem'
                  >
                    Sign Out
                  </Typography>
                  <Typography
                    variant='caption'
                    color='#6b7280'
                  >
                    Logout from your account
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>

          {/* Language Selector (Custom UI) - far right */}
          {/* <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 1 }}>
            <FormControl size='small' sx={{ minWidth: 140 }}>
              <Select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                displayEmpty
                sx={{
                  color: '#fff',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: alpha('#ffffff', 0.3) },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                  '.MuiSelect-icon': { color: '#fff' },
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  borderRadius: 2,
                }}
                renderValue={(val) => {
                  const found = availableLanguages.find((l) => l.code === val);
                  return found ? found.label : 'Language';
                }}
              >
                {availableLanguages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </Box> */}
        </Toolbar>
      </Container>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor='right'
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderLeft: '1px solid',
            borderColor: alpha('#000', 0.08),
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Mobile Menu Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: alpha('#000', 0.06),
            }}
          >
            <Typography
              variant='h6'
              fontWeight={700}
              color='#1a202c'
            >
              Menu
            </Typography>
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              size='small'
              sx={{
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: alpha('#6b7280', 0.1),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Mobile Menu Items */}
          {navItems.map((item, index) => {
            if (item.type === 'single') {
              return (
                <ListItemButton
                  key={item.path}
                  onClick={() => {
                    navigate(item.path!);
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    py: 1.5,
                    backgroundColor: isActive(item.path!) ? alpha('#0050A7', 0.1) : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha('#0050A7', 0.08),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive(item.path!) ? '#0050A7' : '#6b7280',
                      minWidth: 28,
                    }}
                  >
                    {/* {item.icon} */}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.95rem',
                        fontWeight: isActive(item.path!) ? 600 : 500,
                        color: isActive(item.path!) ? '#0050A7' : '#374151',
                      },
                    }}
                  />
                </ListItemButton>
              );
            }

            if (item.type === 'dropdown') {
              return (
                <Accordion
                  key={item.key}
                  elevation={0}
                  disableGutters
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    backgroundColor: isParentActive(item.subItems!) ? alpha('#0050A7', 0.05) : 'transparent',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#6b7280' }} />}
                    sx={{
                      py: 1,
                      px: 2,
                      minHeight: 48,
                      '&:hover': {
                        backgroundColor: alpha('#0050A7', 0.08),
                      },
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        gap: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: isParentActive(item.subItems!) ? '#0050A7' : '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {/* {item.icon} */}
                    </Box>
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: '0.95rem',
                        fontWeight: isParentActive(item.subItems!) ? 600 : 500,
                        color: isParentActive(item.subItems!) ? '#0050A7' : '#374151',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0, px: 1 }}>
                    {item.subItems!.map((subItem) => (
                      <ListItemButton
                        key={subItem.path}
                        onClick={() => {
                          navigate(subItem.path);
                          setMobileMenuOpen(false);
                        }}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          py: 1,
                          pl: 3,
                          backgroundColor: isActive(subItem.path) ? alpha('#0050A7', 0.1) : 'transparent',
                          '&:hover': {
                            backgroundColor: alpha('#0050A7', 0.08),
                            '& .MuiSvgIcon-root': {
                              transform: 'scale(1.1)',
                              filter: 'brightness(1.3) saturate(1.2)',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 28,
                            '& .MuiSvgIcon-root': {
                              color: isActive(subItem.path) ? '#0050A7' : getIconColor(subItem.path),
                              fontSize: '1.1rem',
                              //  transition: "all 0.2s ease-in-out",
                              filter: isActive(subItem.path) ? 'none' : 'brightness(1.1)',
                            },
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.label}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.9rem',
                              fontWeight: isActive(subItem.path) ? 600 : 500,
                              color: isActive(subItem.path) ? '#0050A7' : '#374151',
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            }

            return null;
          })}
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
